<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import plural from 'plural-ru'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()
const starBackgroundImage = `url('${asset('img/3d-star.svg')}')`

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits<{
  (e: 'add-star'): void
}>()

const overnightStarsCount = ref(0)

const background = useTemplateRef<HTMLElement>('background')
const star = useTemplateRef<HTMLElement>('star')
const text = useTemplateRef<HTMLElement>('text')

const opened = ref(false)
let intervalId: ReturnType<typeof setInterval> | null = null

const overnightStarsCountWithNoun = computed(() => {
  return [
    overnightStarsCount.value.toString(),
    plural(overnightStarsCount.value, 'звёздочку', 'звёздочки', 'звёздочек')
  ].join(' ')
})

const animateStarApply = () => {
  if (!props.context.smartbook?.night_stars_applied || !star.value) return

  const animation = star.value.animate(
    [
      { transform: 'scale(1)', filter: 'brightness(1)', color: 'black' },
      { transform: 'scale(1.2)', filter: 'brightness(1.3)', color: 'black', offset: 0.5 },
      { transform: 'scale(1)', filter: 'brightness(1)', color: 'black' }
    ],
    {
      duration: 250,
      easing: 'ease'
    }
  )

  setTimeout(() => emit('add-star'), 125)

  animation.onfinish = () => {
    setTimeout(animateStarApply, 100)
  }
}

const showModalText = () => {
  if (!text.value || !star.value) return

  text.value.removeAttribute('hidden')
  star.value.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-100px)' }], {
    duration: 500,
    easing: 'ease-out',
    fill: 'forwards'
  })
}

const spawnShootingStars = () => {
  if (!background.value) return
  const stars = new Set<HTMLElement>()

  const createStar = () => {
    if (!background.value) return

    const shootingStar = document.createElement('div')
    shootingStar.className = 'student-overnight-stars-modal__shooting-star'
    shootingStar.style.background = `url('${asset('img/student-v2/shooting-star.svg')}')`
    shootingStar.style.backgroundSize = 'cover'
    background.value.appendChild(shootingStar)
    stars.add(shootingStar)

    const startX = Math.random() * (window.innerWidth * 1.4) + window.innerWidth * 0.1
    const startY = Math.random() * 100 - 150
    const duration = Math.random() * 3000 + 1000
    const scale = Math.random() * 0.7 + 0.3
    const opacity = Math.random() * 0.6 + 0.4

    shootingStar.style.left = `${startX}px`
    shootingStar.style.top = `${startY}px`
    shootingStar.style.opacity = String(opacity)

    shootingStar.animate(
      [
        {
          transform: 'translate(0, 0) scale(1)',
          opacity
        },
        {
          transform: `translate(-${window.innerWidth}px, ${window.innerWidth}px) scale(${scale})`,
          opacity: 0
        }
      ],
      {
        duration,
        easing: 'ease-out',
        fill: 'forwards'
      }
    )

    setTimeout(() => {
      shootingStar.remove()
      stars.delete(shootingStar)
    }, duration)
  }

  intervalId = setInterval(() => {
    if (stars.size < 50) createStar()
  }, 100)
}

const dismiss = () => {
  background.value?.setAttribute('hidden', '')
  star.value?.setAttribute('hidden', '')
  text.value?.setAttribute('hidden', '')

  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }

  opened.value = false
  overnightStarsCount.value = 0
}

const $open = () => {
  if (!props.context.smartbook?.night_stars_applied) return
  if (opened.value) return

  opened.value = true
  overnightStarsCount.value = props.context.smartbook.night_stars_applied
  background.value?.removeAttribute('hidden')

  spawnShootingStars()

  setTimeout(() => {
    star.value?.removeAttribute('hidden')
  }, 1500)

  setTimeout(animateStarApply, 2500)
  setTimeout(showModalText, 4500)
}

defineExpose({ $open })

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId)
})
</script>

<template>
  <div class="student-overnight-stars-modal">
    <div ref="background" class="student-overnight-stars-modal__background" hidden />
    <div
      ref="star"
      class="student-overnight-stars-modal__star"
      hidden
      v-text="(context.smartbook || { star_count: 0 }).star_count"
    />

    <div ref="text" class="student-overnight-stars-modal__text" hidden>
      Ура! Ты прослушал аудиоурок в ночном режиме и заработал {{ overnightStarsCountWithNoun }}!
      <v-button mode="primary filled" @click="dismiss"> Отлично! </v-button>
    </div>
  </div>
</template>

<style lang="scss">
.student-overnight-stars-modal {
  position: relative;

  &__background {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 200;

    background-color: rgba(0, 0, 0, 0.8);
    opacity: 1;
    transition:
      background-color 0.5s ease,
      opacity 0.5s ease;
    visibility: visible;

    &[hidden] {
      background-color: rgba(0, 0, 0, 0);
      opacity: 0;
      pointer-events: none;
      display: block;
    }
  }

  .student-overnight-stars-modal__shooting-star {
    position: absolute;
    width: 86px;
    height: 86px;
  }

  &__star {
    position: fixed;
    z-index: 201;
    top: calc(50% - 48px);
    right: calc(50% - 48px);

    display: flex;
    align-items: center;
    justify-content: center;

    width: 96px;
    height: 96px;
    background-image: v-bind(starBackgroundImage);
    background-size: cover;

    font-size: 32px;
    font-weight: bold;
    line-height: 100%;

    transition:
      top 1s ease,
      right 1s ease,
      width 1s ease,
      height 1s ease,
      opacity 0.1s ease;
    transition-delay: 0s;
    transform: translateY(0);
    opacity: 1;

    &[hidden] {
      pointer-events: none;
      opacity: 0;
      display: flex;

      width: 56px;
      height: 56px;
      top: 16px;
      right: 144px;
    }
  }

  &__text {
    width: min(450px, 95%);
    position: fixed;
    z-index: 201;
    top: 50%;
    left: calc(50% - 225px);

    transition:
      transform 0.5s ease,
      opacity 0.5s ease;
    transform: translateY(0);
    opacity: 1;

    @include text-title;
    font-weight: bold;
    text-align: center;
    color: white;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;

    &[hidden] {
      transform: translateY(100px);
      pointer-events: none;
      opacity: 0;
      display: flex;
    }
  }
}
</style>
